/**
 * NoteList Component
 *
 * @component
 * @description This component displays a list of notes in an accordion-style container.
 * It allows adding new notes and managing the visibility of existing notes.
 *
 * @param {NoteData[]} props.notes - An array of NoteData objects representing the notes to be displayed.
 * @returns {JSX.Element} - A JSX element representing the NoteList component.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { isNull } from 'lodash';
import { useTabsMenu } from 'components/TabsNavigation/hooks';
import { LayoutContext } from 'context';
import useNotesContext from 'context/NotesContext';
import { ApproachActionOption } from '../../pages/ValuationsAllocation/components';
import { Tab, TabsNavigation } from '../TabsNavigation';
import Note from './note/Note';
import { StyledButton, StyledTextField } from './styles';
import { NoteData, NoteListProps } from './types';

const NoteList = (props: NoteListProps) => {
  const { pageType, pageTypeId, onAddNote, onDeleteNote, onUpdateNotes } = useNotesContext();
  const { viewOnlyUser } = useContext(LayoutContext);
  const { notes, isDisabled = false, appBarSx } = props;
  const actionsAreDisabled = isDisabled || viewOnlyUser;
  const [visibleNotes, setVisibleNotes] = useState<NoteData[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleMenuOpen, handleMenuClose, menuAnchorEl } = useTabsMenu();

  const handleOnAddNote = useCallback(() => {
    onAddNote?.({ pageType, pageTypeId } as unknown as NoteData);
  }, [onAddNote, pageType, pageTypeId]);

  useEffect(() => {
    if (!isNull(notes)) {
      const tempVisibleNotes = notes.filter(note => !note.is_deleted);
      setVisibleNotes(tempVisibleNotes);
    }
  }, [notes]);

  const getNoteLabel = (note: NoteData, i: number) => note.name || `Note ${i + 1}`;

  const handleDoubleClick = useCallback((note: NoteData) => {
    if (actionsAreDisabled) {
      return;
    }
    const noteId = note.id ? `${note.id}` : `${note.temp_id}`;
    setEditingNoteId(noteId);
    setEditingValue(note.name || '');
  }, [actionsAreDisabled]);

  const handleSaveEdit = useCallback((note: NoteData) => {
    if (!onUpdateNotes) {
      return;
    }
    const updatedNote = { ...note, name: editingValue.trim() || null };
    onUpdateNotes(updatedNote);
    setEditingNoteId(null);
    setEditingValue('');
  }, [editingValue, onUpdateNotes]);

  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditingValue('');
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, note: NoteData) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveEdit(note);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  useEffect(() => {
    if (editingNoteId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingNoteId]);

  useEffect(() => {
    // only allow a single temp note at any given time...

    const hasTempNote = visibleNotes.some(i => i.temp_id);
    const noteTabs: Tab[] = visibleNotes.map((note, i) => {
      const noteId = note.id ? `${note.id}` : `${note.temp_id}`;
      const isEditing = editingNoteId === noteId;
      const displayLabel = getNoteLabel(note, i);

      return {
        content: <Note key={note.id ?? note.temp_id} note={note} isDisabled={actionsAreDisabled} />,
        disabled: actionsAreDisabled,
        disableRipple: isEditing,
        id: noteId,
        label: isEditing ? (
          <StyledTextField
            inputRef={inputRef}
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            onBlur={() => handleSaveEdit(note)}
            onKeyDown={e => handleKeyDown(e, note)}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => e.stopPropagation()}
            variant="standard"
          />
        ) : (
          <StyledButton
            onDoubleClick={() => handleDoubleClick(note)}
            disabled={actionsAreDisabled}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: actionsAreDisabled ? 'default' : 'pointer',
              font: 'inherit',
              color: 'inherit',
            }}>
            {displayLabel}
          </StyledButton>
        ),
        menuItems: [
          ...(onDeleteNote
            ? [
              <ApproachActionOption<NoteData, never>
                key={`delete-${note.id ?? note.temp_id}`}
                actionText="Delete"
                argumentsList={[note]}
                tooltipMessage="Delete Note from the system"
                onClick={() => {
                  onDeleteNote?.({ ...note, is_deleted: true });
                  handleMenuClose();
                }}
              />,
            ]
            : []),
        ],
      };
    });

    if (onAddNote) {
      noteTabs.push({
        id: 'add-button',
        content: <></>,
        disabled: false,
        isActionTab: true,
        label: (
          <Tooltip title={hasTempNote ? 'Only one pending note allowed' : ''}>
            <span>
              <Button onClick={handleOnAddNote} disabled={hasTempNote || actionsAreDisabled}>
                +
              </Button>
            </span>
          </Tooltip>
        ),
      });
    }

    setTabs(noteTabs);

    // exhaustive dependencies are not needed here and result in degraded performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNotes, actionsAreDisabled, editingNoteId, editingValue, handleDoubleClick, handleSaveEdit, handleKeyDown]);

  return (
    <TabsNavigation
      menuAnchorEl={menuAnchorEl}
      mountOnActive
      handleMenuClose={handleMenuClose}
      handleMenuOpen={handleMenuOpen}
      relativeDrawer
      tabs={tabs}
      appBarSx={appBarSx}
    />
  );
};

export default NoteList;
